import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  AgentConversationRequestError,
  abortOntologyAgentOrchestrator,
  abortFactoryMonitoringAgent,
  addTabCustomAgentBulkBatch,
  addTabCustomAgentRun,
  addPendingStagedAttachments,
  addTabStagedAttachments,
  closeTab,
  copyAgentConversation,
  createAgentConversation,
  deleteQueuedOrchestratorMessage,
  deleteAgentConversation,
  disconnectLocalFactoryMonitoringStream,
  disconnectLocalOrchestratorStream,
  editQueuedOrchestratorMessage,
  getStagedAttachmentFile,
  hasNonRetryableStagedAttachmentFailures,
  hasSendingStagedAttachments,
  hydrateAndMigratePersistedTabs,
  loadActiveAgentConversation,
  loadActiveAgentConversationAttachments,
  loadAgentConversations,
  loadQueuedOrchestratorMessages,
  onDomainTopic,
  registerStagedAttachments,
  rejectAbandonedInFlightAgentActions,
  releaseStagedAttachments,
  renameAgentConversation,
  resolveStagedAttachmentFiles,
  resumeOntologyAgentOrchestrator,
  runOntologyAgentOrchestrator,
  setActiveTab,
  setChatComposerModeForConversation,
  setChatComposerModePreference,
  setPendingComposerMode,
  setPendingDraft,
  setPendingEffortSelection,
  setPendingModelSelection,
  setPendingStagedAttachmentStatus,
  setPersistedActiveTabId,
  setPersistedOpenTabIds,
  setTabAttachmentPreviews,
  setTabComposerMode,
  setTabDraft,
  setTabEffortSelection,
  setTabModelSelection,
  setTabStagedAttachmentStatus,
  selectIsTabStreaming,
  STAGED_ATTACHMENT_SEND_ERROR,
  transferPendingStagedAttachments,
  removePendingStagedAttachments,
  removeTabCustomAgentRun,
  removeTabStagedAttachments,
  useAppDispatch,
  useAppSelector,
  useAppStore,
  useListCustomAgentRunsQuery,
  useListRunningCustomAgentBulkBatchesQuery,
  isTabRunLive,
  type AppDispatch,
  type ConversationAttachment,
  type ConversationRun,
  type ConversationSummary,
  type ChatComposerMode,
  type LiveSegment,
  type OrchestratorEffortSelection,
  type OrchestratorModelSelection,
  type QueuedOrchestratorMessage,
  type StagedAttachment
} from '@axilon-apps/domains-ontology-ui-state'
import {
  chatAttachmentPolicyForFilename,
  parseChatWakeupInstruction,
  validateChatAttachmentMetadata,
  validateChatAttachmentTextBytes,
  validateChatAttachmentTotalBytes,
  type ChatAttachmentFailure
} from '@atbash-labs/ontology-agent-wire'
import { useMergedChatAppContext } from '../../chat-app-context'
import { useDocumentVisible } from '../../hooks/use-document-visible'
import { useDebugMode } from '../../lib/debug-mode'
import { isIngestQueryConversationId } from '../IngestSession/ingestQueryConversationStorage'
import { aggregateStepEntityLinks } from './entityLinks'
import {
  composerModeToWireMode,
  CONTINUE_STOPPED_RUN_PROMPT,
  DEFAULT_COMPOSER_MODE
} from './chat-behavior'
import type { SidebarTabDescriptor } from './TabStrip'

/**
 * The loaded transcript's run status field is named `state` (snake-case
 * pass-through from the proxy-server) but the slice's TS interface calls
 * it `status` for compatibility with the synthesized completed-turn
 * shape. Read both so the resume-detection works on either.
 */
function isRunning(turn: ConversationRun): boolean {
  const raw = turn as ConversationRun & { state?: string; status?: string }
  return raw.state === 'running' || raw.status === 'running'
}

/**
 * Re-attach to a top-level orchestrator turn that is still running
 * server-side — after a page reload, or a turn started from another
 * device (e.g. the Prophet phone app). `resumeOntologyAgentOrchestrator`
 * no-ops when this browser already streams the conversation.
 */
function resumeRunningOrchestratorTurn(
  dispatch: AppDispatch,
  conversationId: string,
  runs: ConversationRun[]
): void {
  const runningTurn = runs.find((r) => r.agent_kind === 'agent_orchestrator' && isRunning(r))
  if (runningTurn === undefined) return
  void dispatch(
    resumeOntologyAgentOrchestrator({ conversationId, instruction: runningTurn.prompt })
  )
}

/**
 * Minimum gap between background refreshes of the same target (the
 * conversation list, or one open conversation). Window `focus` and
 * `visibilitychange` usually fire together; this collapses them, and
 * rapid tab switching, into one request.
 */
export const CHAT_BACKGROUND_REFRESH_MIN_INTERVAL_MS = 2_000

function composerPromptText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * Build optimistic `ConversationAttachment` rows from the user's freshly
 * picked `File` objects so the documents panel can render them BEFORE
 * the orchestrator route persists them to the proxy. The synthetic ids
 * are prefixed `pending-` so they can never collide with a
 * proxy-assigned UUID — the slice clears the preview list when the
 * stream resolves, at which point the canonical rows take over.
 *
 * `conversation_id` is left blank because we don't necessarily know it
 * yet (a fresh conversation is created mid-thunk). The panel doesn't
 * read that field, so it doesn't matter at render time.
 */
function synthesizeAttachmentPreviews(
  files: File[],
  clientIds: string[]
): ConversationAttachment[] {
  const nowIso = new Date().toISOString()
  return files.map((file, index) => ({
    id: `pending-${clientIds[index] ?? index}-${file.name}`,
    conversation_id: '',
    run_id: null,
    filename: file.name,
    mime_type: file.type || 'application/octet-stream',
    size_bytes: file.size,
    created_at: nowIso
  }))
}

/** Composer view of a staged file: serializable metadata plus the in-memory `File`. */
export interface StagedAttachmentView extends StagedAttachment {
  file: File | null
}

export function attachmentOnlyInstruction(fileCount: number): string {
  return fileCount === 1 ? 'Review the attached file.' : 'Review the attached files.'
}

function toStagedAttachmentView(items: StagedAttachment[]): StagedAttachmentView[] {
  return items.map((item) => ({
    ...item,
    file: getStagedAttachmentFile(item.clientId) ?? null
  }))
}

function failedStagedAttachment(
  attachment: StagedAttachment,
  failure: ChatAttachmentFailure
): StagedAttachment {
  return {
    ...attachment,
    status: 'failed',
    error: failure.message,
    errorCode: failure.code,
    retryable: failure.retryable,
    errorAction: failure.action
  }
}

function validateNewStagedAttachments(
  existing: StagedAttachment[],
  files: File[],
  registered: StagedAttachment[]
): StagedAttachment[] {
  let acceptedBytes = existing
    .filter((attachment) => attachment.status !== 'failed' || attachment.retryable)
    .reduce((total, attachment) => total + attachment.sizeBytes, 0)
  let acceptedTextBytes = existing
    .filter((attachment) => attachment.status !== 'failed' || attachment.retryable)
    .filter(
      (attachment) => chatAttachmentPolicyForFilename(attachment.filename)?.contentKind === 'text'
    )
    .reduce((total, attachment) => total + attachment.sizeBytes, 0)

  return registered.map((attachment, index) => {
    const file = files[index]
    if (file === undefined) return attachment
    const metadataFailure = validateChatAttachmentMetadata(file, index)
    if (metadataFailure !== null) return failedStagedAttachment(attachment, metadataFailure)

    const policy = chatAttachmentPolicyForFilename(file.name)
    if (policy?.contentKind === 'text') {
      const textFailure = validateChatAttachmentTextBytes(acceptedTextBytes + file.size)
      if (textFailure !== null) {
        return failedStagedAttachment(attachment, { ...textFailure, index })
      }
    }

    const totalFailure = validateChatAttachmentTotalBytes(acceptedBytes + file.size)
    if (totalFailure !== null) {
      return failedStagedAttachment(attachment, { ...totalFailure, index })
    }
    acceptedBytes += file.size
    if (policy?.contentKind === 'text') acceptedTextBytes += file.size
    return attachment
  })
}

export function useOrchestratorChatController(options?: {
  /**
   * `query_agent_only` / `ingest_query_agent_only` skip the planner and
   * dispatch a single agent. Unset (`full`) is the main chat: the composer
   * Ask/Agent dropdown can switch it. Default composer mode is Ask
   * (`query_agent_only` via chat-behavior); Agent runs the full orchestrator.
   */
  mode?: 'full' | 'query_agent_only' | 'ingest_query_agent_only'
  /** Give a direct query / ingest-query run the propose graph-write rail. */
  graphWrite?: boolean
  /** Required when `mode` is `ingest_query_agent_only`. */
  ingestQueryJobId?: string
  /**
   * Isolates an embedded chat surface from the global Chat tab selection.
   * `undefined` uses the global active tab; `null` is a fresh scoped
   * conversation.
   */
  scopedConversationId?: string | null
  /** Called after a fresh scoped conversation is created. */
  onScopedConversationCreated?: (conversationId: string) => void
  /** Called after a copied conversation is focused. */
  onConversationCopied?: (conversationId: string) => void
  /**
   * Route-supplied active tab; wins over the localStorage-persisted one.
   * `null` keeps the new-conversation landing (`/chat`); omit to restore
   * the persisted tab (rail).
   */
  initialActiveTabId?: string | null
}) {
  const configuredMode =
    options?.mode === 'query_agent_only' || options?.mode === 'ingest_query_agent_only'
      ? options.mode
      : 'full'
  const graphWrite = options?.graphWrite === true
  const ingestQueryJobId = options?.ingestQueryJobId
  const scoped = options?.scopedConversationId !== undefined
  const scopedConversationId = options?.scopedConversationId ?? null
  const onScopedConversationCreated = options?.onScopedConversationCreated
  const onConversationCopied = options?.onConversationCopied
  const initialActiveTabId = options?.initialActiveTabId
  const debugMode = useDebugMode()
  const dispatch = useAppDispatch()
  const store = useAppStore()
  // Snapshot-at-send: read the merged route-baseline (+ page overrides) list
  // at submit time so the agent sees the page the user was on when they hit enter.
  const appContext = useMergedChatAppContext()
  const conversations = useAppSelector((s) => s.agentOrchestrator.conversations)
  const conversationsLoaded = useAppSelector((s) => s.agentOrchestrator.conversationsLoaded)
  const openTabs = useAppSelector((s) => s.agentOrchestrator.openTabs)
  const globalActiveTabId = useAppSelector((s) => s.agentOrchestrator.activeTabId)
  const activeTabId = scoped ? scopedConversationId : globalActiveTabId
  const pendingDraft = useAppSelector((s) => s.agentOrchestrator.pendingDraft)
  const pendingModelSelection = useAppSelector((s) => s.agentOrchestrator.pendingModelSelection)
  const pendingEffortSelection = useAppSelector((s) => s.agentOrchestrator.pendingEffortSelection)
  const pendingComposerMode = useAppSelector((s) => s.agentOrchestrator.pendingComposerMode)
  const pendingStagedAttachments = useAppSelector(
    (s) => s.agentOrchestrator.pendingStagedAttachments
  )
  const tabs = useAppSelector((s) => s.agentOrchestrator.tabs)
  const activeTab = useAppSelector((s) =>
    activeTabId === null ? null : (s.agentOrchestrator.tabs[activeTabId] ?? null)
  )
  const { currentData: chatRuns, isLoading: chatRunsLoading } = useListCustomAgentRunsQuery(
    activeTabId
      ? {
          triggered_by_conversation_id: activeTabId,
          include: 'follow_up',
          exclude: 'bulk'
        }
      : undefined,
    { skip: activeTabId === null }
  )
  const { currentData: runningBulkBatches, isLoading: runningBulkBatchesLoading } =
    useListRunningCustomAgentBulkBatchesQuery(activeTabId ?? '', {
      skip: activeTabId === null || !debugMode,
      pollingInterval: 2_000,
      skipPollingIfUnfocused: true
    })
  const customAgentRunsLoading =
    activeTabId !== null &&
    (chatRunsLoading ||
      chatRuns === undefined ||
      (debugMode && (runningBulkBatchesLoading || runningBulkBatches === undefined)) ||
      activeTab?.turnsLoaded !== true)
  const resolvingInitialConversation =
    !scoped && typeof initialActiveTabId === 'string' && activeTabId === null
  const canContribute =
    activeTabId === null ? !resolvingInitialConversation : activeTab?.canContribute === true
  const activeRun = activeTab?.run ?? null
  const [submissionError, setSubmissionError] = useState<string | null>(null)

  // ── Per-tab field projections for the chat pane / ChatTurn props ────
  //
  // Top-level fields below mirror the singleton fields the chat pane
  // used to read directly. Keeping them at the top of the controller's
  // return value (vs. forcing `chat.activeTab?.run?.plan`) keeps the
  // sidebar JSX close to its pre-AXI-3530 shape and minimizes diff in
  // GlobalQuerySidebar.tsx.

  const activeInstruction = activeRun?.instruction ?? null
  // ChatTurn is memoized, so the switch-to-agent callback must stay a
  // stable identity. Read the latest submit + prompt through refs.
  const handleSubmitRef = useRef<
    (
      value: string,
      options?: { forceAgent?: boolean; appContext?: { key: string; value: string }[] }
    ) => Promise<void>
  >(async () => undefined)
  const lastSwitchablePromptRef = useRef('')
  const lastTurnPrompt =
    activeTab === null || activeTab.turns.length === 0
      ? ''
      : (activeTab.turns[activeTab.turns.length - 1]?.prompt ?? '')
  lastSwitchablePromptRef.current =
    composerPromptText(activeInstruction) || composerPromptText(lastTurnPrompt)
  const plan = activeRun?.plan ?? null
  const stepResults = activeRun?.stepResults ?? EMPTY_STEP_RESULTS
  const liveToolCalls = activeRun?.liveToolCalls ?? EMPTY_TOOL_CALLS
  const liveSegmentsByStep = activeRun?.liveSegmentsByStep ?? EMPTY_SEGMENTS_BY_STEP
  const pendingProgressByStep = activeRun?.pendingProgressByStep ?? EMPTY_PENDING_PROGRESS_BY_STEP
  const interStepTextByStep = activeRun?.interStepTextByStep ?? EMPTY_INTER_STEP_TEXT
  const modelFallback = activeRun?.modelFallback ?? null
  const contextCompacted = activeRun?.contextCompacted ?? null
  const inFlightIngestWaits = activeRun?.inFlightIngestWaits ?? EMPTY_INGEST_WAITS
  // Agent-action approval proposals live on the tab (NOT on tab.run) so
  // cards survive the orchestrator stream completing — the propose tool
  // uses async-suspend and the run wraps up while the user is still
  // deciding on each card.
  const inFlightAgentActions = activeTab?.inFlightAgentActions ?? EMPTY_AGENT_ACTIONS
  const customAgentRuns = activeTab?.customAgentRuns ?? EMPTY_CUSTOM_AGENT_RUNS
  const customAgentBulkBatches =
    activeTab?.customAgentBulkBatches ?? EMPTY_CUSTOM_AGENT_BULK_BATCHES
  const deliveredCustomRunIds = useMemo(() => {
    const ids = new Set<string>()
    for (const turn of activeTab?.turns ?? []) {
      const wakeup = parseChatWakeupInstruction(turn.prompt)
      if (wakeup) ids.add(wakeup.runId)
    }
    return ids
  }, [activeTab?.turns])

  useEffect(() => {
    if (!activeTabId || activeTab?.turnsLoaded !== true || chatRuns === undefined) return
    const pending = new Set<string>()
    const recoveredBulkBatches = new Set<string>()
    for (const batch of runningBulkBatches ?? []) {
      recoveredBulkBatches.add(batch.id)
      if (deliveredCustomRunIds.has(batch.id) || customAgentBulkBatches[batch.id]) continue
      dispatch(
        addTabCustomAgentBulkBatch({
          id: activeTabId,
          batch: {
            batchId: batch.id,
            name: batch.agent_name,
            itemCount: batch.item_count
          }
        })
      )
    }
    for (const run of chatRuns) {
      if (run.triggered_by_conversation_id !== activeTabId) continue
      // Bulk children render as one batch card from the dispatch event. Never
      // hydrate them as individual background-agent cards. Recover the grouped
      // card from any child after a page refresh; its polling query fills in
      // the authoritative name, count, and status.
      if (run.trigger_kind === 'bulk') {
        const batchId = run.bulk_batch_id
        if (
          batchId &&
          !recoveredBulkBatches.has(batchId) &&
          !deliveredCustomRunIds.has(batchId) &&
          !customAgentBulkBatches[batchId]
        ) {
          recoveredBulkBatches.add(batchId)
          const snapshot =
            run.config_snapshot && typeof run.config_snapshot === 'object'
              ? run.config_snapshot
              : undefined
          const name =
            snapshot && typeof snapshot.name === 'string' ? snapshot.name : 'Custom agent'
          dispatch(
            addTabCustomAgentBulkBatch({
              id: activeTabId,
              batch: { batchId, name, itemCount: 0 }
            })
          )
        }
        continue
      }
      if (deliveredCustomRunIds.has(run.id)) continue
      pending.add(run.id)
      if (customAgentRuns[run.id]) continue
      const snapshot =
        run.config_snapshot && typeof run.config_snapshot === 'object'
          ? run.config_snapshot
          : undefined
      const name = snapshot && typeof snapshot.name === 'string' ? snapshot.name : 'Custom agent'
      dispatch(
        addTabCustomAgentRun({
          id: activeTabId,
          run: {
            runId: run.id,
            ...(run.custom_agent_config_id ? { configId: run.custom_agent_config_id } : {}),
            name,
            ...(run.parameters ? { parameters: run.parameters } : {}),
            kind: run.trigger_kind === 'follow_up' ? 'follow_up' : 'chat',
            ...(run.parent_run_id ? { parentRunId: run.parent_run_id } : {})
          }
        })
      )
    }
    for (const runId of Object.keys(customAgentRuns)) {
      if (!pending.has(runId)) {
        dispatch(removeTabCustomAgentRun({ id: activeTabId, runId }))
      }
    }
  }, [
    activeTab?.turnsLoaded,
    activeTabId,
    chatRuns,
    customAgentBulkBatches,
    customAgentRuns,
    deliveredCustomRunIds,
    dispatch,
    runningBulkBatches
  ])
  const liveFinalSummary = activeRun?.liveFinalSummary ?? null
  const streamError = activeRun?.streamError ?? submissionError
  const cancelRequested = activeRun?.cancelRequested ?? false
  // Settled runs keep `tab.run` for the error/stop banner, but the SSE
  // consumer is gone — show Continue, not Stop/Cancelling.
  const isStreaming = isTabRunLive(activeRun)
  const activeConversationAttachments = activeTab?.attachments ?? EMPTY_ATTACHMENTS
  const activeConversationAttachmentsLoaded = activeTab?.attachmentsLoaded ?? false
  const liveTurnAttachmentPreviews = activeTab?.liveTurnAttachmentPreviews ?? EMPTY_ATTACHMENTS
  const queuedMessages = activeTab?.queuedMessages ?? EMPTY_QUEUED_MESSAGES

  const liveSummaryLinkGroups = useMemo(
    () => aggregateStepEntityLinks(Object.values(stepResults)),
    [stepResults]
  )

  // Tab-strip summaries — one entry per open tab. Joins `openTabs[]`
  // against `tabs[id]` (streaming state) and `conversations[]` (title).
  const openTabSummaries = useMemo<SidebarTabDescriptor[]>(() => {
    return openTabs
      .filter((id: string) => {
        // Keep per-job Ask lanes out of the Chat tab strip (they are not in
        // the conversations list and are remembered client-side).
        if (isIngestQueryConversationId(id)) return false
        const summary = conversations.find((c: ConversationSummary) => c.id === id)
        if (summary?.origin === 'ingest_query') return false
        return true
      })
      .map((id: string) => {
        const tab = tabs[id]
        const summary = conversations.find((c: ConversationSummary) => c.id === id)
        return {
          id,
          kind: 'chat' as const,
          title: summary?.title ?? 'Loading…',
          isStreaming: isTabRunLive(tab?.run),
          hasError: tab?.run?.streamError != null,
          readOnly: tab?.canContribute === false
        }
      })
  }, [openTabs, tabs, conversations])

  // ── Composer draft (routed to pending vs. per-tab) ──────────────────

  const [scopedPendingDraft, setScopedPendingDraft] = useState('')
  const [scopedPendingModelSelection, setScopedPendingModelSelection] =
    useState<OrchestratorModelSelection | null>(null)
  const [scopedPendingEffortSelection, setScopedPendingEffortSelection] =
    useState<OrchestratorEffortSelection | null>(null)
  const [scopedPendingComposerMode, setScopedPendingComposerMode] =
    useState<ChatComposerMode | null>(null)
  const [scopedPendingStagedAttachments, setScopedPendingStagedAttachments] = useState<
    StagedAttachment[]
  >([])
  const scopedPendingStagedRef = useRef<StagedAttachment[]>([])
  scopedPendingStagedRef.current = scopedPendingStagedAttachments
  const draft =
    activeTabId !== null ? (activeTab?.draft ?? '') : scoped ? scopedPendingDraft : pendingDraft

  const setDraft = useCallback(
    (value: string): void => {
      if (!canContribute) return
      if (activeTabId === null && scoped) {
        setScopedPendingDraft(value)
      } else if (activeTabId === null) {
        dispatch(setPendingDraft(value))
      } else {
        dispatch(setTabDraft({ id: activeTabId, draft: value }))
      }
    },
    [activeTabId, canContribute, dispatch, scoped]
  )

  // ── Composer model selector (routed to pending vs. per-tab, like draft) ──

  const modelSelection =
    activeTabId !== null
      ? (activeTab?.modelSelection ?? null)
      : scoped
        ? scopedPendingModelSelection
        : pendingModelSelection

  const setModelSelection = useCallback(
    (selection: OrchestratorModelSelection | null): void => {
      if (!canContribute) return
      if (activeTabId === null && scoped) {
        setScopedPendingModelSelection(selection)
      } else if (activeTabId === null) {
        dispatch(setPendingModelSelection(selection))
      } else {
        dispatch(setTabModelSelection({ id: activeTabId, selection }))
      }
    },
    [activeTabId, canContribute, dispatch, scoped]
  )

  const effortSelection =
    activeTabId !== null
      ? (activeTab?.effortSelection ?? null)
      : scoped
        ? scopedPendingEffortSelection
        : pendingEffortSelection

  const setEffortSelection = useCallback(
    (selection: OrchestratorEffortSelection | null): void => {
      if (!canContribute) return
      if (activeTabId === null && scoped) {
        setScopedPendingEffortSelection(selection)
      } else if (activeTabId === null) {
        dispatch(setPendingEffortSelection(selection))
      } else {
        dispatch(setTabEffortSelection({ id: activeTabId, selection }))
      }
    },
    [activeTabId, canContribute, dispatch, scoped]
  )

  // Ask/Agent is per-conversation composer state, like draft/model/effort.
  // A thread with no stored mode, and every new chat, start in Ask.
  const composerMode: ChatComposerMode =
    activeTabId !== null
      ? (activeTab?.composerMode ?? DEFAULT_COMPOSER_MODE)
      : scoped
        ? (scopedPendingComposerMode ?? DEFAULT_COMPOSER_MODE)
        : (pendingComposerMode ?? DEFAULT_COMPOSER_MODE)
  const mode = configuredMode === 'full' ? composerModeToWireMode(composerMode) : configuredMode

  const persistComposerMode = useCallback(
    (next: ChatComposerMode, conversationId: string | null): void => {
      setChatComposerModePreference(next)
      if (conversationId === null) return
      setChatComposerModeForConversation(conversationId, next)
    },
    []
  )

  const setAskModeEnabled = useCallback(
    (enabled: boolean): void => {
      if (configuredMode !== 'full' || !canContribute) return
      const next: ChatComposerMode = enabled ? 'ask' : 'agent'
      persistComposerMode(next, activeTabId)
      if (activeTabId === null && scoped) {
        setScopedPendingComposerMode(next)
      } else if (activeTabId === null) {
        dispatch(setPendingComposerMode(next))
      } else {
        dispatch(setTabComposerMode({ id: activeTabId, mode: next }))
      }
    },
    [activeTabId, canContribute, configuredMode, dispatch, persistComposerMode, scoped]
  )

  // ── Local component state ────────────────────────────────────────────

  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteInFlight, setDeleteInFlight] = useState(false)
  const [copyInFlight, setCopyInFlight] = useState(false)
  const [copyError, setCopyError] = useState<string | null>(null)
  const [attachmentLoadError, setAttachmentLoadError] = useState<string | null>(null)
  // Prompt last submitted by the Switch to Agent mode button. The live
  // follow-up hides that duplicate user bubble even before the Ask turn
  // is in `pastTurns` (reload / later history uses `isAgentModeSwitchRerun`).
  const [agentModeSwitchPrompt, setAgentModeSwitchPrompt] = useState<string | null>(null)
  const agentModeSwitchPromptRef = useRef<string | null>(null)
  agentModeSwitchPromptRef.current = agentModeSwitchPrompt

  // ── Freshness: chats change on other devices too ─────────────────────
  //
  // Another browser or the phone app can create a conversation or add turns
  // to one open here, and nothing pushes that to this page. So every explicit
  // list view (page mount, rail expand, history open) re-reads the list, and
  // returning to the window re-reads the list plus the active conversation.
  // `conversationsLoaded` only gates the first-load skeleton.
  const lastBackgroundRefreshRef = useRef(new Map<string, number>())
  const claimBackgroundRefresh = useCallback((key: string): boolean => {
    const now = Date.now()
    const last = lastBackgroundRefreshRef.current.get(key)
    if (last !== undefined && now - last < CHAT_BACKGROUND_REFRESH_MIN_INTERVAL_MS) return false
    lastBackgroundRefreshRef.current.set(key, now)
    return true
  }, [])

  const loadConversations = useCallback(async (): Promise<void> => {
    lastBackgroundRefreshRef.current.set('conversations', Date.now())
    await dispatch(loadAgentConversations())
  }, [dispatch])

  /**
   * Re-read an open rail conversation so turns added elsewhere appear.
   * `openTab` merges the fetched turns without touching the tab's draft,
   * staged files, or in-flight run, and the GET applies the server's
   * orphaned-run reconciliation and `canContribute`. Skipped while this
   * browser streams the conversation (the run's own completion refetches
   * it) and for hidden or still-hydrating tabs.
   */
  const refreshOpenConversation = useCallback(
    (id: string): void => {
      const state = store.getState()
      if (!state.agentOrchestrator.openTabs.includes(id)) return
      if (state.agentOrchestrator.tabs[id]?.turnsLoaded !== true) return
      if (selectIsTabStreaming(state, id)) return
      if (!claimBackgroundRefresh(`conversation:${id}`)) return
      void dispatch(loadActiveAgentConversation(id))
        .then((conv) => {
          if (selectIsTabStreaming(store.getState(), id)) return
          resumeRunningOrchestratorTurn(dispatch, id, conv.runs)
        })
        .catch((err: unknown) => {
          console.warn(`[orchestrator] refresh conversation ${id} failed`, err)
        })
    },
    [claimBackgroundRefresh, dispatch, store]
  )

  useEffect(() => {
    if (scoped) return
    const onReturn = (): void => {
      if (document.visibilityState === 'hidden') return
      const state = store.getState()
      const activeId = state.agentOrchestrator.activeTabId
      if (activeId !== null && selectIsTabStreaming(state, activeId)) return
      // Only once something has shown the list; the first view loads it.
      if (state.agentOrchestrator.conversationsLoaded && claimBackgroundRefresh('conversations')) {
        void dispatch(loadAgentConversations()).catch((err: unknown) => {
          console.warn('[orchestrator] conversation-list refresh failed', err)
        })
      }
      if (activeId !== null) refreshOpenConversation(activeId)
    }
    window.addEventListener('focus', onReturn)
    document.addEventListener('visibilitychange', onReturn)
    return () => {
      window.removeEventListener('focus', onReturn)
      document.removeEventListener('visibilitychange', onReturn)
    }
  }, [claimBackgroundRefresh, dispatch, refreshOpenConversation, scoped, store])

  const stagedAttachmentMeta: StagedAttachment[] =
    activeTabId !== null
      ? (activeTab?.stagedAttachments ?? EMPTY_STAGED_ATTACHMENTS)
      : scoped
        ? scopedPendingStagedAttachments
        : pendingStagedAttachments
  const stagedAttachments = toStagedAttachmentView(stagedAttachmentMeta)
  const hasSendingAttachments = hasSendingStagedAttachments(stagedAttachmentMeta)
  const hasInvalidAttachments = hasNonRetryableStagedAttachmentFailures(stagedAttachmentMeta)

  const markStagedStatus = useCallback(
    (
      clientIds: string[],
      status: StagedAttachment['status'],
      error?: string | null,
      failure?: ChatAttachmentFailure
    ): void => {
      if (clientIds.length === 0) return
      if (activeTabId !== null) {
        dispatch(
          setTabStagedAttachmentStatus({
            id: activeTabId,
            clientIds,
            status,
            error,
            errorCode: failure?.code,
            retryable: failure?.retryable,
            errorAction: failure?.action
          })
        )
        return
      }
      if (scoped) {
        const idSet = new Set(clientIds)
        setScopedPendingStagedAttachments((prev) =>
          prev.map((item) =>
            idSet.has(item.clientId)
              ? {
                  ...item,
                  status,
                  error: status === 'failed' ? (error ?? null) : null,
                  errorCode: status === 'failed' ? (failure?.code ?? null) : null,
                  retryable: status === 'failed' ? (failure?.retryable ?? true) : false,
                  errorAction: status === 'failed' ? (failure?.action ?? 'retry') : undefined
                }
              : item
          )
        )
        return
      }
      dispatch(
        setPendingStagedAttachmentStatus({
          clientIds,
          status,
          error,
          errorCode: failure?.code,
          retryable: failure?.retryable,
          errorAction: failure?.action
        })
      )
    },
    [activeTabId, dispatch, scoped]
  )

  useEffect(() => {
    if (!scoped) return
    const previousJobId = ingestQueryJobId
    return () => {
      if (previousJobId === undefined) return
      const ids = scopedPendingStagedRef.current.map((item) => item.clientId)
      if (ids.length > 0) releaseStagedAttachments(ids)
      setScopedPendingStagedAttachments([])
    }
  }, [ingestQueryJobId, scoped])

  const addAttachments = useCallback(
    (files: File[]): void => {
      if (!canContribute || files.length === 0) return
      const registered = validateNewStagedAttachments(
        stagedAttachmentMeta,
        files,
        registerStagedAttachments(files)
      )
      if (activeTabId !== null) {
        dispatch(addTabStagedAttachments({ id: activeTabId, attachments: registered }))
        return
      }
      if (scoped) {
        setScopedPendingStagedAttachments((prev) => [...prev, ...registered])
        return
      }
      dispatch(addPendingStagedAttachments(registered))
    },
    [activeTabId, canContribute, dispatch, scoped, stagedAttachmentMeta]
  )

  const removeAttachment = useCallback(
    (clientId: string): void => {
      if (!canContribute) return
      const current =
        activeTabId !== null
          ? (tabs[activeTabId]?.stagedAttachments ?? [])
          : scoped
            ? scopedPendingStagedRef.current
            : pendingStagedAttachments
      const item = current.find((attachment) => attachment.clientId === clientId)
      if (item === undefined || item.status === 'sending') return
      releaseStagedAttachments([clientId])
      if (activeTabId !== null) {
        dispatch(
          removeTabStagedAttachments({
            id: activeTabId,
            clientIds: [clientId]
          })
        )
        return
      }
      if (scoped) {
        setScopedPendingStagedAttachments((prev) =>
          prev.filter((attachment) => attachment.clientId !== clientId)
        )
        return
      }
      dispatch(removePendingStagedAttachments({ clientIds: [clientId] }))
    },
    [activeTabId, canContribute, dispatch, pendingStagedAttachments, scoped, tabs]
  )

  const clearAttachments = useCallback((): void => {
    if (!canContribute) return
    const ids = stagedAttachmentMeta.map((item) => item.clientId)
    if (ids.length === 0) return
    releaseStagedAttachments(ids)
    if (activeTabId !== null) {
      dispatch(removeTabStagedAttachments({ id: activeTabId, clientIds: ids }))
      return
    }
    if (scoped) {
      setScopedPendingStagedAttachments([])
      return
    }
    dispatch(removePendingStagedAttachments({ clientIds: ids }))
  }, [activeTabId, canContribute, dispatch, scoped, stagedAttachmentMeta])

  // ── Hydration: restore open tabs + active tab on mount ──────────────
  //
  // Single-shot effect that runs once at mount. Migrates the legacy
  // single-tab key into the new openTabs[] / activeTabId[] keys, opens
  // every persisted tab in parallel, focuses the route tab (or the
  // persisted active when the route does not specify one), and kicks off
  // /resume against any tab whose newest orchestrator run is still
  // `running` server-side. Failed loads (deleted conversations, network
  // errors) close the tab locally so a stale id doesn't keep 404ing.
  // `initialActiveTabId: null` is `/chat`'s new-conversation landing.

  const hasHydratedRef = useRef(false)
  useEffect(() => {
    if (scoped) return
    if (hasHydratedRef.current) return
    hasHydratedRef.current = true

    const { openTabIds, activeTabId: persistedActive } = hydrateAndMigratePersistedTabs()
    const routeSpecifiesTab = initialActiveTabId !== undefined
    const routeTabId = typeof initialActiveTabId === 'string' ? initialActiveTabId : null

    // `/chat` is an explicit new-conversation landing — never restore the
    // persisted focused tab. Still load open tabs so the list and any
    // in-flight runs stay available.
    if (routeSpecifiesTab && routeTabId === null) {
      dispatch(setActiveTab({ id: null }))
    }

    if (openTabIds.length === 0 && !routeSpecifiesTab) return
    if (openTabIds.length === 0 && routeTabId === null) return

    const idsToLoad = [...openTabIds]
    if (routeTabId !== null && !idsToLoad.includes(routeTabId)) {
      idsToLoad.push(routeTabId)
    }
    if (idsToLoad.length === 0) return

    const loadedIds = new Set<string>()
    void Promise.allSettled(
      idsToLoad.map(async (id) => {
        try {
          const conv = await dispatch(loadActiveAgentConversation(id))
          loadedIds.add(id)
          resumeRunningOrchestratorTurn(dispatch, id, conv.runs)
        } catch (err) {
          console.warn(`[orchestrator] failed to rehydrate tab ${id}`, err)
          dispatch(closeTab({ id }))
        }
      })
    ).then(() => {
      if (routeSpecifiesTab) {
        if (routeTabId !== null && loadedIds.has(routeTabId)) {
          dispatch(setActiveTab({ id: routeTabId }))
        } else {
          dispatch(setActiveTab({ id: null }))
        }
        return
      }
      if (persistedActive !== null) {
        dispatch(setActiveTab({ id: persistedActive }))
      }
    })
  }, [dispatch, scoped, initialActiveTabId])

  // ── Persist tab state to localStorage on change ─────────────────────

  useEffect(() => {
    if (scoped) return
    setPersistedOpenTabIds(openTabs.filter((id: string) => !isIngestQueryConversationId(id)))
  }, [openTabs, scoped])
  useEffect(() => {
    if (scoped) return
    setPersistedActiveTabId(activeTabId)
  }, [activeTabId, scoped])

  // ── Auto-load attachments for the active tab ────────────────────────

  useEffect(() => {
    if (activeTabId === null) {
      setAttachmentLoadError(null)
      return
    }
    const tab = tabs[activeTabId]
    if (tab?.attachmentsLoaded) {
      setAttachmentLoadError(null)
      return
    }
    let cancelled = false
    setAttachmentLoadError(null)
    void dispatch(loadActiveAgentConversationAttachments(activeTabId)).catch((err) => {
      if (cancelled) return
      console.warn('[orchestrator] load conversation attachments failed', err)
      setAttachmentLoadError(
        err instanceof Error ? err.message : 'Failed to load attachments for this conversation.'
      )
    })
    return () => {
      cancelled = true
    }
    // Intentionally narrow deps to activeTabId — `tabs` is read inside the
    // effect only for the `attachmentsLoaded` short-circuit and changes on
    // every reducer dispatch, which would re-run the effect way more
    // often than needed.
  }, [activeTabId, dispatch])

  // Redis is the queue source of truth. Every tab loads on open (restoring
  // the rail after a page refresh), when a turn starts or ends, and on each
  // chat-queue wake-up, which also carries follow-ups another browser queued,
  // edited or removed. Hidden pages don't refresh and reload when shown
  // again; a 403/404 means access is gone, so refreshing stops.
  const documentVisible = useDocumentVisible()
  useEffect(() => {
    if (activeTabId === null || !documentVisible) return
    let stopped = false
    const refresh = (): void => {
      if (stopped) return
      void dispatch(loadQueuedOrchestratorMessages(activeTabId)).catch((err) => {
        if (stopped) return
        if (
          err instanceof AgentConversationRequestError &&
          (err.status === 403 || err.status === 404)
        ) {
          stopped = true
          return
        }
        console.warn('[orchestrator] queued-message refresh failed', err)
      })
    }
    refresh()
    const offChatQueue = onDomainTopic('chat-queue', refresh)
    return () => {
      stopped = true
      offChatQueue()
    }
  }, [activeTabId, dispatch, documentVisible, isStreaming])

  // ── Handlers ────────────────────────────────────────────────────────

  const canContributeToConversation = useCallback(
    (id: string): boolean => {
      const tab = tabs[id]
      if (tab !== undefined) return tab.canContribute
      return conversations.find((conversation) => conversation.id === id)?.canContribute === true
    },
    [conversations, tabs]
  )

  const handleSubmit = useCallback(
    async (
      value: string,
      options?: { forceAgent?: boolean; appContext?: { key: string; value: string }[] }
    ): Promise<void> => {
      if (!canContribute) return
      const forceAgent = options?.forceAgent === true
      if (forceAgent) setAskModeEnabled(false)
      else setAgentModeSwitchPrompt(null)

      const trimmed = composerPromptText(value)
      const submitted = forceAgent ? [] : stagedAttachmentMeta
      if (!trimmed && (forceAgent || submitted.length === 0)) return
      if (hasSendingStagedAttachments(submitted)) return
      if (hasNonRetryableStagedAttachmentFailures(submitted)) return
      setSubmissionError(null)

      const submittedIds = submitted.map((item) => item.clientId)
      const resolved = resolveStagedAttachmentFiles(submittedIds)
      if (submittedIds.length > 0 && resolved.missingClientIds.length > 0) {
        markStagedStatus(submittedIds, 'failed', STAGED_ATTACHMENT_SEND_ERROR)
        setSubmissionError(STAGED_ATTACHMENT_SEND_ERROR)
        return
      }
      const filesForTurn = resolved.files
      const effectiveInstruction =
        trimmed.length > 0 ? trimmed : attachmentOnlyInstruction(filesForTurn.length)
      const titleSeed =
        trimmed.length === 0 && filesForTurn[0] !== undefined ? filesForTurn[0].name : undefined

      markStagedStatus(submittedIds, 'sending')

      let targetId: string
      if (activeTabId === null) {
        let created
        try {
          created = await dispatch(
            createAgentConversation({
              instruction: effectiveInstruction,
              ...(titleSeed !== undefined ? { titleSeed } : {}),
              ...(mode === 'ingest_query_agent_only' ? { origin: 'ingest_query' as const } : {})
            })
          )
        } catch (err) {
          markStagedStatus(submittedIds, 'failed', STAGED_ATTACHMENT_SEND_ERROR)
          if (scoped) {
            setScopedPendingDraft(trimmed)
          } else {
            dispatch(setPendingDraft(trimmed))
          }
          setSubmissionError(
            err instanceof Error ? err.message : 'Failed to create the conversation.'
          )
          console.warn('[orchestrator] pre-create failed', err)
          return
        }
        targetId = created.id
        if (scoped) {
          const idSet = new Set(submittedIds)
          const moving = scopedPendingStagedRef.current
            .filter((item) => idSet.has(item.clientId))
            .map((item) => ({
              ...item,
              status: 'sending' as const,
              error: null,
              errorCode: null,
              retryable: false,
              errorAction: undefined
            }))
          if (moving.length > 0) {
            dispatch(addTabStagedAttachments({ id: targetId, attachments: moving }))
          }
          setScopedPendingStagedAttachments((prev) =>
            prev.filter((item) => !idSet.has(item.clientId))
          )
          onScopedConversationCreated?.(targetId)
          setScopedPendingDraft('')
          setScopedPendingComposerMode(null)
        } else {
          dispatch(
            transferPendingStagedAttachments({
              conversationId: targetId,
              clientIds: submittedIds
            })
          )
          dispatch(setActiveTab({ id: targetId }))
          dispatch(setPendingDraft(''))
          dispatch(setPendingComposerMode(null))
        }
      } else {
        targetId = activeTabId
      }

      if (configuredMode === 'full') {
        const nextMode: ChatComposerMode = forceAgent ? 'agent' : composerMode
        dispatch(setTabComposerMode({ id: targetId, mode: nextMode }))
        persistComposerMode(nextMode, targetId)
      }

      if (filesForTurn.length > 0) {
        dispatch(
          setTabAttachmentPreviews({
            id: targetId,
            previews: synthesizeAttachmentPreviews(filesForTurn, submittedIds)
          })
        )
      }

      // The user moved on without explicitly approving / rejecting any
      // open cypher-write proposals on this tab — treat them as abandoned
      // so they don't keep coming back through the post-turn hydrate
      // (which pulls every still-`awaiting_user` row from the proxy and
      // would otherwise re-populate the sidebar with stale cards). This
      // also applies while a turn is live: the new message queues behind
      // that turn, which stays blocked until its open cards are decided.
      // Only touches the target tab; other tabs' open proposals are
      // unaffected.
      try {
        await dispatch(rejectAbandonedInFlightAgentActions(targetId))
      } catch (err) {
        console.warn('[orchestrator] reject-abandoned cypher-writes failed', err)
      }

      try {
        const runMode = forceAgent ? composerModeToWireMode('agent') : mode
        await dispatch(
          runOntologyAgentOrchestrator({
            instruction: effectiveInstruction,
            conversationId: targetId,
            attachments: filesForTurn.length > 0 ? filesForTurn : undefined,
            attachmentClientIds: submittedIds.length > 0 ? submittedIds : undefined,
            ...((options?.appContext ?? appContext).length > 0
              ? { appContext: options?.appContext ?? appContext }
              : {}),
            ...(debugMode ? { debugMode: true } : {}),
            ...(runMode !== 'full' ? { mode: runMode } : {}),
            ...(graphWrite ? { graphWrite: true } : {}),
            ...(runMode === 'ingest_query_agent_only' && ingestQueryJobId
              ? { ingestQueryJobId }
              : {})
          })
        )
      } catch (err) {
        setSubmissionError(err instanceof Error ? err.message : 'Failed to send the message.')
      }
    },
    [
      activeTabId,
      appContext,
      canContribute,
      composerMode,
      configuredMode,
      debugMode,
      dispatch,
      graphWrite,
      ingestQueryJobId,
      markStagedStatus,
      mode,
      onScopedConversationCreated,
      persistComposerMode,
      scoped,
      setAskModeEnabled,
      stagedAttachmentMeta
    ]
  )
  handleSubmitRef.current = handleSubmit

  const handleSwitchToAgentMode = useCallback(
    (prompt: string): void => {
      if (configuredMode !== 'full' || !canContribute) return
      const trimmed = composerPromptText(prompt) || lastSwitchablePromptRef.current
      if (!trimmed) return
      if (agentModeSwitchPromptRef.current === trimmed) return
      setAskModeEnabled(false)
      agentModeSwitchPromptRef.current = trimmed
      setAgentModeSwitchPrompt(trimmed)
      void handleSubmitRef.current(trimmed, { forceAgent: true })
    },
    [canContribute, configuredMode, setAskModeEnabled]
  )

  const handleStop = useCallback((): void => {
    if (activeTabId === null || !canContribute) return
    if (activeTab?.run?.runKind === 'factory_monitoring') {
      void dispatch(abortFactoryMonitoringAgent(activeTabId))
      return
    }
    void dispatch(abortOntologyAgentOrchestrator(activeTabId))
  }, [activeTabId, activeTab?.run?.runKind, canContribute, dispatch])

  const handleEditQueuedMessage = useCallback(
    async (messageId: string, instruction: string): Promise<void> => {
      if (activeTabId === null || !canContribute) return
      await dispatch(editQueuedOrchestratorMessage(activeTabId, messageId, instruction))
    },
    [activeTabId, canContribute, dispatch]
  )

  const handleDeleteQueuedMessage = useCallback(
    async (messageId: string): Promise<void> => {
      if (activeTabId === null || !canContribute) return
      await dispatch(deleteQueuedOrchestratorMessage(activeTabId, messageId))
    },
    [activeTabId, canContribute, dispatch]
  )

  const handleStartNewChatFromThis = useCallback(async (): Promise<void> => {
    if (activeTabId === null || copyInFlight) return
    setCopyError(null)
    setCopyInFlight(true)
    try {
      const copied = await dispatch(copyAgentConversation(activeTabId))
      onConversationCopied?.(copied.id)
    } catch (err) {
      const status =
        typeof err === 'object' && err !== null && 'status' in err
          ? (err as { status?: unknown }).status
          : undefined
      setCopyError(
        status === 409
          ? 'Wait for the current response to finish, then try again.'
          : 'Couldn’t start a new chat from this conversation. Try again.'
      )
    } finally {
      setCopyInFlight(false)
    }
  }, [activeTabId, copyInFlight, dispatch, onConversationCopied])

  const handleForkFromHere = useCallback(
    async (throughRunId: string): Promise<void> => {
      if (activeTabId === null || copyInFlight || throughRunId.trim() === '') return
      setCopyInFlight(true)
      try {
        const copied = await dispatch(
          copyAgentConversation(activeTabId, {
            throughRunId: throughRunId.trim()
          })
        )
        onConversationCopied?.(copied.id)
      } catch (err) {
        const status =
          typeof err === 'object' && err !== null && 'status' in err
            ? (err as { status?: unknown }).status
            : undefined
        toast.error(
          status === 409
            ? 'Wait for the current response to finish, then try again.'
            : 'Couldn’t start a new chat from this location. Try again.'
        )
      } finally {
        setCopyInFlight(false)
      }
    },
    [activeTabId, copyInFlight, dispatch, onConversationCopied]
  )

  const handleNewTab = useCallback((): void => {
    // Carry the current conversation's explicit model into the pending
    // composer state. A new conversation has no tab until its first message,
    // so this is the state that seeds the tab and its first request.
    dispatch(setPendingModelSelection(modelSelection))
    dispatch(setPendingEffortSelection(effortSelection))
    // New chats always start in Ask, even if the thread you just left was Agent.
    dispatch(setPendingComposerMode(DEFAULT_COMPOSER_MODE))
    dispatch(setActiveTab({ id: null }))
  }, [dispatch, effortSelection, modelSelection])

  const handleSwitchTab = useCallback(
    (id: string): void => {
      if (id === activeTabId) return
      dispatch(setActiveTab({ id }))
      refreshOpenConversation(id)
    },
    [activeTabId, dispatch, refreshOpenConversation]
  )

  /**
   * Close a tab. Decision #7: tears down the local SSE consumer for the
   * conversation but does NOT POST `/agent-orchestrator/cancel`, so the
   * server-side orchestrator keeps running. Reopening the conversation
   * later re-attaches via `/resume`.
   */
  const handleCloseTab = useCallback(
    (id: string): void => {
      const stagedIds = tabs[id]?.stagedAttachments.map((item) => item.clientId) ?? []
      if (stagedIds.length > 0) releaseStagedAttachments(stagedIds)
      if (tabs[id]?.run?.runKind === 'factory_monitoring') {
        disconnectLocalFactoryMonitoringStream(id)
      } else {
        disconnectLocalOrchestratorStream(id)
      }
      dispatch(closeTab({ id }))
    },
    [dispatch, tabs]
  )

  /**
   * Open-or-focus a conversation from the history rail. If the
   * conversation is already in `openTabs`, focus its cached transcript and
   * re-read it in the background (turns may have been added on another
   * device). Otherwise load + open + focus. Returns `false` when the load fails so
   * the chat page can degrade a pasted unknown id to the landing.
   */
  const handleSelectFromHistory = useCallback(
    async (id: string): Promise<boolean> => {
      if (openTabs.includes(id)) {
        dispatch(setActiveTab({ id }))
        refreshOpenConversation(id)
        return true
      }
      try {
        await dispatch(loadActiveAgentConversation(id))
        dispatch(setActiveTab({ id }))
        return true
      } catch (err) {
        console.warn('[orchestrator] load conversation failed', err)
        return false
      }
    },
    [openTabs, dispatch, refreshOpenConversation]
  )

  const handleRequestDelete = useCallback(
    (id: string): void => {
      if (!canContributeToConversation(id)) return
      setDeleteError(null)
      setPendingDeleteId(id)
    },
    [canContributeToConversation]
  )

  const handleRename = useCallback(
    (id: string, title: string): void => {
      if (!canContributeToConversation(id)) return
      void dispatch(renameAgentConversation(id, title)).catch((err: unknown) => {
        console.warn('[orchestrator] rename conversation failed', err)
      })
    },
    [canContributeToConversation, dispatch]
  )

  /**
   * Delete the underlying conversation entirely. Distinct from
   * `handleCloseTab` (decision #7): delete is a destructive act on the
   * conversation, so any in-flight run IS cancelled server-side via the
   * /cancel POST before the row is removed.
   */
  const handleConfirmDelete = useCallback(async (): Promise<void> => {
    if (pendingDeleteId === null) return
    const id = pendingDeleteId
    if (!canContributeToConversation(id)) {
      setPendingDeleteId(null)
      return
    }
    setDeleteInFlight(true)
    setDeleteError(null)
    try {
      const stagedIds = tabs[id]?.stagedAttachments.map((item) => item.clientId) ?? []
      if (stagedIds.length > 0) releaseStagedAttachments(stagedIds)
      if (tabs[id]?.run !== null && tabs[id]?.run !== undefined) {
        if (tabs[id]?.run?.runKind === 'factory_monitoring') {
          await dispatch(abortFactoryMonitoringAgent(id))
        } else {
          await dispatch(abortOntologyAgentOrchestrator(id))
        }
      }
      await dispatch(deleteAgentConversation(id))
      if (openTabs.includes(id)) {
        // closeTab handles the activeTabId fallback (left neighbor or null).
        dispatch(closeTab({ id }))
      }
      setPendingDeleteId(null)
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete conversation.')
    } finally {
      setDeleteInFlight(false)
    }
  }, [pendingDeleteId, openTabs, tabs, dispatch, canContributeToConversation])

  const handleCancelDelete = useCallback((): void => {
    if (deleteInFlight) return
    setPendingDeleteId(null)
    setDeleteError(null)
  }, [deleteInFlight])

  const handleContinueStoppedRun = useCallback((): void => {
    if (!canContribute) return
    void handleSubmitRef.current(CONTINUE_STOPPED_RUN_PROMPT)
  }, [canContribute])

  // ── Derived view flags ──────────────────────────────────────────────

  const pastTurns = useMemo(() => {
    if (activeTab === null) return EMPTY_TURNS
    return activeTab.turns.filter((t: ConversationRun) => t.status !== 'running')
  }, [activeTab])
  const showLiveRow = isStreaming
  // The last turn ended because the user hit Stop, so offer to pick the
  // work back up. The agent gets the stopped run's partial transcript,
  // so continuing resumes rather than restarts.
  const showContinueStoppedRun =
    canContribute && !isStreaming && pastTurns[pastTurns.length - 1]?.status === 'aborted'
  // Scoped Ask lanes briefly have a conversation id before the transcript
  // lands — don't flash the "no turns yet" / welcome empty states.
  const awaitingScopedTranscript =
    scoped && activeTabId !== null && (activeTab === null || !activeTab.turnsLoaded)
  const showEmptyState =
    !showLiveRow && pastTurns.length === 0 && !streamError && !awaitingScopedTranscript
  const pendingDeleteConversation =
    pendingDeleteId === null
      ? null
      : (conversations.find((c: ConversationSummary) => c.id === pendingDeleteId) ?? null)

  return {
    // Tab-strip state + handlers
    openTabs,
    openTabSummaries,
    activeTabId,
    activeTab,
    canContribute,
    handleNewTab,
    handleSwitchTab,
    handleCloseTab,
    handleSelectFromHistory,

    // Conversation rail
    conversations,
    conversationsLoaded,
    loadConversations,
    handleRename,

    // Composer
    draft,
    setDraft,
    modelSelection,
    setModelSelection,
    effortSelection,
    setEffortSelection,
    askMode: mode === 'query_agent_only',
    setAskMode: setAskModeEnabled,
    askModeLocked: configuredMode !== 'full',
    stagedAttachments,
    hasSendingAttachments,
    hasInvalidAttachments,
    addAttachments,
    removeAttachment,
    clearAttachments,

    // Active tab field projections (read by the chat pane / ChatTurn)
    activeInstruction,
    plan,
    stepResults,
    liveToolCalls,
    liveSegmentsByStep,
    pendingProgressByStep,
    interStepTextByStep,
    modelFallback,
    contextCompacted,
    inFlightIngestWaits,
    inFlightAgentActions,
    customAgentRuns,
    customAgentBulkBatches,
    customAgentRunsLoading,
    liveFinalSummary,
    streamError,
    cancelRequested,
    isStreaming,
    activeConversationAttachments,
    activeConversationAttachmentsLoaded,
    liveTurnAttachmentPreviews,
    queuedMessages,
    attachmentLoadError,
    liveSummaryLinkGroups,
    pastTurns,
    showLiveRow,
    showEmptyState,
    agentModeSwitchPrompt,
    showContinueStoppedRun,

    // Submit / Stop
    handleSubmit,
    handleSwitchToAgentMode,
    handleStop,
    handleContinueStoppedRun,
    handleEditQueuedMessage,
    handleDeleteQueuedMessage,
    handleStartNewChatFromThis,
    handleForkFromHere,
    copyInFlight,
    copyError,

    // Delete dialog
    pendingDeleteId,
    pendingDeleteConversation,
    deleteInFlight,
    deleteError,
    handleRequestDelete,
    handleConfirmDelete,
    handleCancelDelete,

    // Misc
    dispatch
  }
}

// Frozen empty defaults reused across renders so the controller's return
// value doesn't change identity when the active tab has no run / no
// attachments. Prevents downstream `useMemo` deps from invalidating on
// every render.
const EMPTY_STEP_RESULTS: Readonly<Record<number, never>> = Object.freeze({})
const EMPTY_TOOL_CALLS = Object.freeze([]) as never[]
const EMPTY_SEGMENTS_BY_STEP: Readonly<Record<number, LiveSegment[]>> = Object.freeze({})
const EMPTY_PENDING_PROGRESS_BY_STEP: Readonly<Record<number, string>> = Object.freeze({})
const EMPTY_INTER_STEP_TEXT: Readonly<Record<number, string>> = Object.freeze({})
const EMPTY_INGEST_WAITS: Readonly<Record<string, never>> = Object.freeze({})
const EMPTY_AGENT_ACTIONS: Readonly<Record<string, never>> = Object.freeze({})
const EMPTY_CUSTOM_AGENT_RUNS: Readonly<Record<string, never>> = Object.freeze({})
const EMPTY_CUSTOM_AGENT_BULK_BATCHES: Readonly<Record<string, never>> = Object.freeze({})
const EMPTY_ATTACHMENTS = Object.freeze([]) as unknown as ConversationAttachment[]
const EMPTY_STAGED_ATTACHMENTS = Object.freeze([]) as unknown as StagedAttachment[]
const EMPTY_QUEUED_MESSAGES = Object.freeze([]) as unknown as QueuedOrchestratorMessage[]
const EMPTY_TURNS = Object.freeze([]) as unknown as ConversationRun[]

// Re-export so test fixtures can mention the same `ConversationSummary`
// type the sidebar expects in its tab strip.
export type { ConversationSummary }
